import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { INSTITUTIONS_SUPERVISION } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { EnTetePage } from '../../commun/EnTetePage';
import { DelaisReseaux } from './DelaisReseaux';
import { CartePilotage } from './CartePilotage';
const mesures = {
  incidents: 'Incidents enregistrés',
  incidents_ouverts: 'Incidents ouverts',
  incidents_critiques: 'Incidents critiques ouverts',
  signalements: 'Signalements reçus',
  signalements_a_qualifier: 'À qualifier',
  interventions: 'Interventions enregistrées',
  rapports_a_valider: 'Rapports à valider',
  equipements: 'Équipements',
  maintenances_en_retard: 'Maintenances en retard',
};
interface Ligne {
  province_code: string;
  organisation: string;
  province: string;
  domaine: string;
  fictive: boolean;
  incidents: number;
  incidents_ouverts: number;
  incidents_critiques: number;
  signalements: number;
  signalements_a_qualifier: number;
  interventions: number;
  rapports_a_valider: number;
  equipements: number;
  maintenances_en_retard: number;
}
export function PagePilotageReseaux({ accueil = false }: { accueil?: boolean } = {}) {
  const { contexte } = useSession();
  const institution = contexte?.appartenance_active?.institution;
  const permis = Boolean(
    institution && (INSTITUTIONS_SUPERVISION as readonly string[]).includes(institution),
  );
  const [province, setProvince] = useState('');
  const [domaine, setDomaine] = useState('');
  const [fictives, setFictives] = useState(import.meta.env.VITE_CC_ENVIRONNEMENT === 'presentation');
  const [erreur, setErreur] = useState('');
  const [exportEnCours, setExportEnCours] = useState(false);
  const filtres = {
    province_id: province || undefined,
    domaine: domaine || undefined,
    inclure_fictives: fictives ? 'oui' : 'non',
  };
  const q = useQuery({
    queryKey: ['pilotage-reseaux', contexte?.appartenance_active?.id, filtres],
    queryFn: () =>
      requete<{ perimetre: string; calcule_le: string; tronque: boolean; elements: Ligne[] }>(
        '/supervision/reseaux',
        { parametres: filtres },
      ),
    enabled: permis,
    staleTime: 0,
    refetchInterval: 60_000,
  });
  const provinces = useQuery({
    queryKey: ['provinces'],
    queryFn: () => requete<{ elements: { id: string; nom: string }[] }>('/referentiel/provinces'),
    enabled: permis,
  });
  if (!permis) return <p role="alert">Cet espace est réservé aux institutions de supervision.</p>;
  return (
    <section>
      {accueil ? <h2>Situation nationale des réseaux</h2> : <EnTetePage titre="Pilotage des réseaux" />}
      <p>
        État courant des données enregistrées. Les organismes sans données réseau ne figurent pas
        dans ce tableau ; l’absence de ligne ne signifie pas l’absence d’incident sur le territoire.
      </p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label>
          Province{' '}
          <select value={province} onChange={(e) => setProvince(e.target.value)}>
            <option value="">Mon périmètre</option>
            {provinces.data?.elements.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nom}
              </option>
            ))}
          </select>
        </label>
        <label>
          Service{' '}
          <select value={domaine} onChange={(e) => setDomaine(e.target.value)}>
            <option value="">Eau et électricité</option>
            <option value="WATER">Eau</option>
            <option value="ELECTRICITY">Électricité</option>
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={fictives}
            onChange={(e) => setFictives(e.target.checked)}
          />
          Inclure les organismes de démonstration
        </label>
        <button onClick={() => void q.refetch()}>Actualiser</button>
      </div>
      {(erreur || q.error || provinces.error) && (
        <p role="alert">{erreur || messageErreur(q.error || provinces.error)}</p>
      )}
      {q.isPending && <p>Calcul en cours…</p>}
      {q.data && (
        <>
          <p>
            Périmètre : {q.data.perimetre} · calculé le{' '}
            {new Date(q.data.calcule_le).toLocaleString('fr-FR')}
          </p>
          {q.data.tronque && <p role="alert">Plus de 2 000 lignes : affinez les filtres.</p>}
          <CartePilotage lignes={q.data.elements} tronque={q.data.tronque} />
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
              gap: 16,
            }}
          >
            {Object.entries(mesures).map(([k, v]) => (
              <div key={k}>
                <dt>{v}</dt>
                <dd>
                  {q.data.elements.reduce((s, l) => s + Number(l[k as keyof typeof mesures]), 0)}
                </dd>
              </div>
            ))}
          </dl>
          {(['csv', 'xlsx', 'pdf'] as const).map((format) => (
            <button
              key={format}
              disabled={
                exportEnCours ||
                q.isFetching ||
                q.data.tronque ||
                (format === 'pdf' && q.data.elements.length > 200)
              }
              onClick={async () => {
                setErreur('');
                setExportEnCours(true);
                try {
                  const r = await requete<Response>(
                    format === 'pdf'
                      ? '/supervision/reseaux/rapport.pdf'
                      : '/supervision/reseaux/export',
                    {
                      parametres: { ...filtres, ...(format === 'pdf' ? {} : { format }) },
                      brut: true,
                    },
                  );
                  const url = URL.createObjectURL(await r.blob());
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `pilotage-reseaux.${format}`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  setExportEnCours(false);
                }
              }}
            >
              {format === 'pdf'
                ? 'Télécharger le rapport PDF'
                : `Exporter le tableau ${format === 'csv' ? 'CSV' : 'Excel'}`}
            </button>
          ))}
          {exportEnCours && (
            <p role="status">
              Préparation du téléchargement… Le rapport PDF peut prendre quelques secondes.
            </p>
          )}
          {q.data.elements.length > 200 && (
            <p>Le rapport PDF est limité à 200 lignes. Affinez les filtres ou utilisez Excel.</p>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table>
              <caption>Volumes agrégés par exploitant, province et service</caption>
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>Province</th>
                  <th>Service</th>
                  {Object.values(mesures).map((v) => (
                    <th key={v}>{v}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.data.elements.map((l, i) => (
                  <tr key={`${l.organisation}-${l.province}-${l.domaine}-${i}`}>
                    <td>
                      {l.organisation}
                      {l.fictive ? ' (démonstration)' : ''}
                    </td>
                    <td>{l.province}</td>
                    <td>{l.domaine === 'WATER' ? 'Eau' : 'Électricité'}</td>
                    {Object.keys(mesures).map((k) => (
                      <td key={k}>{l[k as keyof typeof mesures]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <DelaisReseaux filtres={filtres} contexte={contexte?.appartenance_active?.id ?? ''} />
    </section>
  );
}
